import * as React from 'react';
import { useSession } from 'next-auth/react';

import {
  approvalsApi,
  RequestsApiError
} from '~/entities/EIB/Approval/api/api';
import { api } from '~/shared/services/api';
import { UserProfile, UserProfileContextData } from '~/shared/types/profile';

const UserProfileContext = React.createContext<
  UserProfileContextData | undefined
>(undefined);

interface UserProfileProviderProps {
  children: React.ReactNode;
  initialProfile?: UserProfile | null;
}

export function UserProfileProvider({
  children,
  initialProfile = null
}: UserProfileProviderProps) {
  const [profile, setProfile] = React.useState<UserProfile | null>(
    initialProfile
  );
  const [isEibZoneSpocUser, setIsEibZoneSpocUser] = React.useState<boolean>(
    initialProfile?.profileType === 'Zone Spoc' || false
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { data: session } = useSession();

  const fetchProfile = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data } = await api.get<UserProfile>(
        '/api/user-access-management/user/profile'
      );
      setProfile(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkEIBZoneSpocUser = React.useCallback(async (): Promise<void> => {
    if (!window.location.pathname.startsWith('/eib')) {
      return;
    }

    try {
      await approvalsApi.getRequests();
      setIsEibZoneSpocUser(true);
    } catch (err) {
      if (
        err instanceof RequestsApiError &&
        (err.statusCode === 400 ||
          err.statusCode === 401 ||
          err.statusCode === 403 ||
          err.statusCode === 500)
      ) {
        setIsEibZoneSpocUser(false);
        return;
      }
    }
  }, []);

  const proxiedAsEmployeeId = session?.user?.proxiedAs?.employeeId;
  const previousProxiedAsRef = React.useRef(proxiedAsEmployeeId);

  React.useEffect(() => {
    const hasProxyChanged =
      previousProxiedAsRef.current !== proxiedAsEmployeeId;
    previousProxiedAsRef.current = proxiedAsEmployeeId;

    if (initialProfile && !hasProxyChanged) {
      return;
    }

    void fetchProfile();

    // Only check EIB Zone SPOC user when in EIB context
    if (typeof window !== 'undefined') {
      const isEibRoute = window.location.pathname.startsWith('/eib');
      if (isEibRoute) {
        void checkEIBZoneSpocUser();
      }
    }
  }, [fetchProfile, checkEIBZoneSpocUser, initialProfile, proxiedAsEmployeeId]);

  const value: UserProfileContextData = {
    profile,
    isEibZoneSpocUser,
    isLoading,
    error,
    refetch: fetchProfile,
    checkEIBZoneSpocUser
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile(): UserProfileContextData {
  const context = React.useContext(UserProfileContext);

  return (
    context || {
      profile: null,
      isLoading: false,
      error: null,
      refetch: () => Promise.resolve(),
      checkEIBZoneSpocUser: () => Promise.resolve(),
      isEibZoneSpocUser: false
    }
  );
}
